import React, { useState, useReducer, useCallback, useRef, useEffect } from 'react';
import { useMutation, gql } from '@apollo/client';
import { queryGetMobileBuilds } from '../TabMobileBuildsList/TabMobileBuildsList.js';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import cn from 'classnames';
import ReactGA from 'Shared/analytics';

import Modal from 'Components/Modal/Modal';
import Input from 'Components/Input/Input';

import FileUploader from '../FileUploader/FileUploader';
import { logger } from 'RootShared/utility/ErrorLogger';

const cutUrl = (url) => {
  const domain = 'https://storage.googleapis.com/arcm-production-static';
  const tempUrl = url.split('?')[0];
  return tempUrl.replace(domain, '');
};

const BUILD_TYPES = [
  { value: 'DEV', label: 'Development' },
  { value: 'STAGING', label: 'Staging' },
  { value: 'TEST', label: 'Test' },
  { value: 'PROD', label: 'Production' },
];

const NEW_BUILD = {
  clientContract: '',
  description: '',
  buildType: '',
  system: '',
  appFileName: '',
  changelogName: '',
};

export const mutationAddMobileBuilds = gql`
  mutation createBuild($input: CreateBuildMutationInput!) @api(name: root) {
    createBuild(input: $input) {
      build {
        id
        clientContract {
          id
          title
        }
        system
        shareLink
        buildType
        createdAt
      }
      errors {
        field
      }
    }
  }
`;

const AddMobileBuildForm = (props) => {
  const { data, handleOpenAddBuildModal, projectName } = props;
  const { id } = useParams();
  const toastId = useRef(null);

  const sendFileMethod = process.env.SEND_FILE_METHOD;

  const [newMobileBuild, setMobileBuild] = useReducer(
    (oldState, newState) => ({ ...oldState, ...newState }),
    NEW_BUILD
  );
  const [isFormValid, setFormValid] = useState();
  const [appFile, setAppFile] = useState();
  const [logFile, setLogFile] = useState();

  const sendFileToGoogleBucket = async (file) => {
    if (!file) return Promise.resolve(null);

    let reqHeader = new Headers();
    reqHeader.append('Content-Type', file.file.type);
    return fetch(file.url, {
      method: 'PUT',
      headers: reqHeader,
      processData: false,
      crossDomain: true,
      body: file.file,
    }).then((response) => {
      if (response.ok) {
        return cutUrl(file.url);
      }
    });
  };

  const [addNewMobileBuilds, {loading}] = useMutation(mutationAddMobileBuilds, {
    onCompleted(data) {
      onRequestBuildAddSuccess(data);
    },
    onError(error) {
      logger.error(error);
      onRequestBuildAddError(error);
    },
    update(cache, { data: { createBuild } }) {
      const { builds } = cache.readQuery({
        query: queryGetMobileBuilds,
        variables: {
          offset: 0,
          limit: 40,
          clientContractId: id ? +id : +data.clientContract.id,
        },
      });
      if (builds) {
        const newBuilds = [createBuild.build, ...builds];
        cache.modify({
          fields: {
            builds() {
              return newBuilds;
            },
          },
        });
      }
    },
  });

  const onRequestBuildAddSuccess = useCallback(
    (data) => {
      if (!data.errors) {
        toast.update(toastId.current, {
          render: 'New build successfully added',
          type: toast.TYPE.SUCCESS,
          autoClose: 3000,
          closeButton: true,
          hideProgressBar: false,
        });
      }
    },
    [toastId]
  );

  const onRequestBuildAddError = useCallback(
    (error) => {
      ReactGA.exception({
        description: error.message,
        fatal: true,
      });
      toast.update(toastId.current, {
        render: 'Error during saving request',
        type: toast.TYPE.ERROR,
        autoClose: 3000,
        closeButton: true,
        hideProgressBar: false,
      });
      return error;
    },
    [toastId]
  );

  useEffect(() => {
    if (newMobileBuild.buildType !== '' && document.querySelectorAll('.chozenFile')[0]) {
      setFormValid(true);
    }
  }, [newMobileBuild, setMobileBuild, appFile, setAppFile]);

  const handleMobileBuildAdd = async (e) => {
    ReactGA.event({
      category: 'Mobile Builds',
      action: 'Submit a build',
    });

    const contractId = id ? id : data.clientContract.id;
    const mobileBuild = {
      ...newMobileBuild,
      clientContract: +contractId,
    };

    toastId.current = toast('In progress, please wait...', {
      hideProgressBar: true,
    });

    if(!loading){
      toast.dismiss(toastId.current);
    }

    

    if (sendFileMethod === 'google-storage') {
      Promise.all([sendFileToGoogleBucket(appFile), sendFileToGoogleBucket(logFile)])
        .then(([appFileName, changelogName]) => {
          addNewMobileBuilds({
            variables: {
              input: {
                ...mobileBuild,
                appFileName,
                changelogName,
                system: appFile.system,
              },
            },
          });
        })
        .catch((error) => onRequestBuildAddError(error));
    } else {
      addNewMobileBuilds({ variables: { input: mobileBuild } });
    }

    handleOpenAddBuildModal();

    e.preventDefault();
  };

  return (
    <Modal>
      <form onSubmit={handleMobileBuildAdd} data-testid="addMobileBuild">
        <div className="modal-bg">
          <div className="modal" data-testid="add-mobile-build-popup">
            <div className="modal__build-head">
              <h2>{projectName}</h2>
              <span onClick={handleOpenAddBuildModal} className="icon-close" />
            </div>
            <div className="modal__build-info">
              <Input
                label="Build type"
                variant="select"
                options={BUILD_TYPES}
                callback={(event) => {
                  setMobileBuild({ buildType: event.value.toLowerCase() });
                }}
                id={'buildInput'}
                fieldProps={{
                  name: 'buildType',
                }}
              />

              <Input
                label="Description"
                variant="textarea"
                callback={(event) => {
                  setMobileBuild({ description: event.target.value });
                }}
                id={'descInput'}
              />
              <FileUploader
                data={data}
                name={'Application'}
                acceptTypes={'.ipa, .apk'}
                inputId={'appInputFile'}
                fileFormatHelper={'Please choose .apk or .ipa type file'}
                getBase64={(file) => {
                  setMobileBuild(file);
                }}
                getFile={(file) => {
                  setAppFile(file);
                }}
                sendFileMethod={sendFileMethod}
              />
              <FileUploader
                data={data}
                name={'Changelog'}
                acceptTypes={'.pdf, .doc, .docx, .txt'}
                inputId={'logInputFile'}
                fileFormatHelper={'Please select a .doc, .docx, .txt or .pdf document'}
                getBase64={(file) => setMobileBuild(file)}
                getFile={(file) => setLogFile(file)}
                sendFileMethod={sendFileMethod}
              />
              <input
                className={cn('button button_primary button_no-icon', !isFormValid && 'add-build-btn__disabled')}
                value="Add new mobile build"
                type="submit"
                data-testid="add-build-button"
                disabled={isFormValid ? false : true}
              />
            </div>
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default AddMobileBuildForm;
